import { useMutation, useQuery } from "@apollo/client";
import {
  CreateInboxMessageClassificationDocument,
  InboxMessageClassificationsDocument,
  useMyAccountQuery,
  useMyInvokablePlaybooksQuery,
} from "@qw/graphql";
import { AUTO_RUN_PLAYBOOK_ALLOWLIST } from "@qw/qw-common";
import { Button, Icon, useModal, useToast } from "@qw/ui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";
import { ClassificationForm, createClassificationValidationSchema } from "./classification-form";

interface Classification {
  id: string;
  label: string;
  description: string;
  source: "System" | "User";
  associatedPlaybookId?: string | null;
  playbook?: { id: string; name: string } | null;
  autoRunPlaybook?: boolean;
}

export const NewClassificationButton = () => {
  const { data: me } = useMyAccountQuery();
  const myEmail = me?.myAccount?.email?.toLowerCase() ?? "";
  // The allowlist keeps today's allowlisted users enabled regardless of LD state
  // (LD only ever *adds*), matching the API's `default = allowlist.includes(email)`
  // semantics.
  const flags = useFlags<Record<string, boolean | undefined>>();
  const canAutoRunPlaybook =
    AUTO_RUN_PLAYBOOK_ALLOWLIST.includes(myEmail) || flags["enable-classification-autorun"] === true;
  const { data } = useQuery<{ inboxMessageClassifications: Classification[] }>(InboxMessageClassificationsDocument);
  // Classifications auto-run a playbook at the top level with no parent
  // orchestrator context. myInvokablePlaybooks returns owned + subscribed
  // filtered to type=Workflow server-side, so the picker only ever offers
  // playbooks the classification fire-path can actually dispatch.
  const { data: invokablePlaybooksData } = useMyInvokablePlaybooksQuery();

  const [createMutation, { loading: creating }] = useMutation(CreateInboxMessageClassificationDocument, {
    refetchQueries: ["InboxMessageClassifications"],
  });

  const { openForm } = useModal();

  const { userClassifications } = useMemo(() => {
    const list = data?.inboxMessageClassifications ?? [];
    return {
      systemClassifications: list.filter((c) => c.source === "System"),
      userClassifications: list.filter((c) => c.source === "User"),
    };
  }, [data]);

  const playbookOptions = useMemo(() => {
    return (invokablePlaybooksData?.myInvokablePlaybooks ?? [])
      .map((p) => ({ id: p.id, name: p.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [invokablePlaybooksData]);

  const { toast } = useToast();

  const openCreateForm = () => {
    openForm(
      <ClassificationForm
        mode="create"
        initial={{ label: "", description: "", associatedPlaybookId: null, autoRunPlaybook: false }}
        playbookOptions={playbookOptions}
        submitLabel="Create"
        disabled={creating}
        canAutoRunPlaybook={canAutoRunPlaybook}
        userClassifications={userClassifications}
        renderFieldsOnly
        onSubmit={() => {}}
      />,
      {
        title: "Create Classification",
        type: "dialog",
        validationSchema: createClassificationValidationSchema("create", userClassifications),
        defaultValues: {
          label: "",
          description: "",
          associatedPlaybookId: "__none__",
          autoRunPlaybook: false,
        },
        onSubmit: async (values, close) => {
          const normalized = {
            label: values.label.trim(),
            description: (values.description || "").trim(),
            associatedPlaybookId: values.associatedPlaybookId === "__none__" ? null : values.associatedPlaybookId,
            autoRunPlaybook: canAutoRunPlaybook ? Boolean(values.autoRunPlaybook) : undefined,
          };
          await createMutation({
            variables: {
              input: normalized,
            },
          });
          close();
          toast({ title: `Classification created` });
        },
      }
    );
  };

  return (
    <Button variant="outline" size="sm" onClick={openCreateForm} disabled={creating}>
      <Icon icon="add" />
      New
    </Button>
  );
};
